import React, { useState } from "react";
import Container from "@/components/marketing/Container";
import { ExternalLink } from "lucide-react";

/**
 * Earned-coverage card on a dark band. Tag pill, logo stage (grayscale → colour
 * on hover), serif headline, descriptor, and a "View live article" link in
 * #7200CB. If no logo is present, the publication name renders as styled text.
 */
function EarnedCoverageCard({ sample }) {
  const [logoError, setLogoError] = useState(false);
  return (
    <a
      href={sample.url}
      target="_blank"
      rel="noopener noreferrer"
      className="bg-white rounded-2xl p-7 flex flex-col shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-200 group/card"
    >
      {/* Tag pill */}
      <span className="inline-block self-start text-[10px] font-medium uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#f1eff5] text-[#6b6880]">
        {sample.tag}
      </span>
      {/* Logo stage */}
      <div className="mt-5 h-[80px] bg-[#fafafa] rounded-lg flex items-center justify-center overflow-hidden">
        {sample.logo && !logoError ? (
          <img
            src={sample.logo}
            alt={sample.outlet}
            className="max-h-10 max-w-[160px] w-auto grayscale opacity-60 group-hover/card:grayscale-0 group-hover/card:opacity-100 transition-all duration-300"
            onError={() => setLogoError(true)}
          />
        ) : (
          <span className="font-heading text-sm font-semibold text-steel text-center px-6">{sample.outlet}</span>
        )}
      </div>
      {/* Headline (serif — this section only) */}
      <h3
        className="text-[22px] font-semibold text-[#14121c] leading-snug mt-6 mb-2"
        style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
      >
        {sample.title}
      </h3>
      {/* Descriptor */}
      <p className="text-[13.5px] text-[#6b6880] leading-snug mb-6 flex-1">{sample.descriptor}</p>
      {/* Link pinned to bottom */}
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#7200CB]">
        View live article <ExternalLink className="w-4 h-4" />
      </span>
    </a>
  );
}

export default function SamplePlacementsSplit({ samples, tag, title }) {
  return (
    <section className="bg-showcase relative overflow-hidden">
      {/* Soft purple radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse 80% 60% at 50% 90%, rgba(114,0,203,0.06) 0%, transparent 70%)",
        }}
      />
      <Container className="relative py-7 sm:py-9 lg:py-12">
        <div className="text-center mb-12 lg:mb-16">
          {tag && (
            <span className="inline-block font-mono text-xs uppercase tracking-widest px-2.5 py-1 rounded-full border border-[#3a3352] text-[#8a86a8] mb-4">
              {tag}
            </span>
          )}
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">{title}</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto items-stretch">
          {samples.map((sample) => (
            <EarnedCoverageCard key={sample.url} sample={sample} />
          ))}
        </div>
      </Container>
    </section>
  );
}